import { Query } from '@jupiterone/jupiter-types/question';
import { Tab, Tabs } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { solo_ListQuestions_complianceRequirement_questions_queries } from 'jupiter-web-compliance/src/graphql/__generated__/solo_ListQuestions';
import React, { useEffect, useState } from 'react';
import { Badge, queryCausesGap } from '../../..';
import { JupiterTheme } from '../../../theme/types';
import SortablePaginationQueryResult from '../../QueryResult/SortablePaginationQueryResult/SortablePaginationQueryResult';
import QueryTabContent from './QueryTab';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      '&:first': {
        marginTop: 0,
      },
    },
    tabList: {
      background: theme.palette.background.dark,
      marginBottom: theme.spacing(0),
    },
    tabListRoot: {},
    tab: {
      fontFamily: `'Ubuntu Mono', monospace`,
      whiteSpace: 'nowrap',
      width: theme.spacing(30),
      minHeight: theme.spacing(0),
    },
    tabWrapper: { display: 'block' },
    tabIndicator: {
      backgroundColor: theme.palette.background.dark,
    },
    gapIndicator: {
      right: theme.spacing(-1),
    },
    queryContainer: {
      height: 'inherit',
    },
    graphView: {
      minHeight: '500px',
    },
  })
);

interface Props {
  queries: (
    | solo_ListQuestions_complianceRequirement_questions_queries
    | Query
  )[];
  className: string;
  onChangeActiveQuery?: (query: string) => void;
  onRemoveResult?: VoidFunction;
  standardId?: string;
  showGapIndicators?: boolean;
  parentSelectedRowID?: string | null;
  parentSetSelectedRowID?: (id: string | null) => void;
}

interface BooleanMap {
  [key: number]: boolean;
}

const TabQueryResultList = ({
  queries,
  onChangeActiveQuery,
  showGapIndicators,
  ...props
}: Props) => {
  const classes = useStyles();
  const [loadedQueries, setLoadedQueries] = useState<BooleanMap>({});
  const [selectedQueryIndex, setSelectedQueryIndex] = useState<number>(0);

  useEffect(() => {
    if (onChangeActiveQuery && selectedQueryIndex) {
      onChangeActiveQuery(queries[selectedQueryIndex].query);
    }
  }, [selectedQueryIndex]);

  const handleQueryLoad = (index: number) => () => {
    setLoadedQueries({ ...loadedQueries, [index]: true });
  };

  return (
    <div className={classes.root}>
      {queries.length > 1 && (
        <Tabs
          value={selectedQueryIndex}
          onChange={(_event: any, newValue: number) =>
            setSelectedQueryIndex(newValue)
          }
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabList}
          classes={{ root: classes.tabListRoot }}
        >
          {queries.map((q, i) => (
            <Tab
              key={i}
              classes={{
                root: classes.tab,
                wrapper: classes.tabWrapper,
              }}
              label={
                <Badge
                  invisible={!showGapIndicators || !queryCausesGap(q as Query)}
                  status="ERROR"
                  anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                  size="SMALL"
                  classes={{
                    badge: classes.gapIndicator,
                  }}
                >
                  <QueryTabContent
                    query={q as Query}
                    index={i}
                    selected={selectedQueryIndex === i}
                  />
                </Badge>
              }
            />
          ))}
        </Tabs>
      )}
      <div className={classes.queryContainer}>
        {queries.map((query, i) => (
          <SortablePaginationQueryResult
            {...props}
            classes={{
              graphView: classes.graphView,
            }}
            key={`${query.query}.${i}`}
            query={query}
            onLoaded={handleQueryLoad(i)}
            showResult={selectedQueryIndex === i}
          />
        ))}
      </div>
    </div>
  );
};
export default TabQueryResultList;
